import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import MainComponent from "../MainComponent";
import Login from "../Login";
import Select from "react-select";
import { getRandomColor, createImageFromInitials } from "../Utils";
import { url } from "../../store/sagas/API.js";
import "./BiFunctions.css";
import { useDispatch, useSelector } from "react-redux";
import { getEmployeeData, getFunctionsDetails, postStrategyFunctions, postCustomerFunctionsFromMaster } from "../../store/actions";
import { AiOutlineSearch } from "react-icons/ai";
import { Button } from "react-bootstrap";
import Alert from "../Alert";
import Swal from "sweetalert2";
import Drawer from "@mui/material/Drawer";
import keys from "../../utils/helpInfoKeys.json";
import SideNavHelpInfo from "../../utils/SideNavHelpInfo.js";


function BiFunctions() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [functions, setFunctions] = useState([]);
  const [empData, setEmpData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [showAddFuncModal, setShowAddFuncModal] = useState(false);
  const [selectedMasterIds, setSelectedMasterIds] = useState([]);
  const [masterSearchTerm, setMasterSearchTerm] = useState("");

  const selectedPermission = useSelector((state) => state.selectedPermission);
  const getFunctionsData = useSelector((state) => state.getFunctionsDetails);
  const getEmployeeDetails = useSelector((state) => state.getEmployeeDetails);
  const addFunctionsFromMasterData = useSelector((state) => state.addFunctionsFromMasterData);
  const selectedHelpInfoSidebar = useSelector(
    (state) => state.selectedHelpInfoSidebar
  );

  const customFilter = (option, searchText) =>
    option.data.label.toLowerCase().includes(searchText.toLowerCase());


  useEffect(() => {
    const biId = localStorage.getItem("bi_id");
    const productId = localStorage.getItem("product_id");
    dispatch(getEmployeeData(biId));
    if (biId && productId) {
      dispatch(getFunctionsDetails(biId, productId));
    }
  }, [dispatch]);

  useEffect(() => {
    if (getFunctionsData && getFunctionsData.functions) {
      setFunctions(getFunctionsData.functions);
      setLoading(false);
    }
  }, [getFunctionsData]);

  useEffect(() => {
    if (addFunctionsFromMasterData && addFunctionsFromMasterData.status === 200) {
      const biId = localStorage.getItem("bi_id");
      const productId = localStorage.getItem("product_id");
      if (biId && productId) {
        dispatch(getFunctionsDetails(biId, productId));
      }
      setShowAddFuncModal(false);
      setSelectedMasterIds([]);
    }
  }, [addFunctionsFromMasterData, dispatch]);

  useEffect(() => {
    if (getEmployeeDetails && getEmployeeDetails.emp_data) {
      setEmpData(
        getEmployeeDetails.emp_data.map((obj) => ({
          value: obj.id,
          label: obj.prop_name,
          icon: obj.icon
            ? url + obj.icon
            : createImageFromInitials(
                30,
                obj.prop_name,
                getRandomColor(obj.prop_name)
              ),
        }))
      );
    }
  }, [getEmployeeDetails]);

  const filteredFunctions = useMemo(() => {
    return functions.filter((fn) => {
      if (!showInactive && !fn.is_active) return false;
      if (searchTerm.trim())
        return fn.name.toLowerCase().includes(searchTerm.toLowerCase());
      return true;
    });
  }, [functions, searchTerm, showInactive]);

  const handleStatusChange = (id, isActive) => {
    // When changing TO inactive (isActive = false)
    if (!isActive) {
      const fn = functions.find((f) => f.id === id);
      if (fn && fn.task_count > 0) {
        // Show confirmation alert for functions with tasks
        Alert.warning({
          title: "Confirm Inactivation",
          html: `
            <div style="text-align:center; margin: 0; ">
              <p style="margin-top:0;">If the function <span style = "font-weight: var(--heading-font-weight)" >${fn.name}</span> is inactivated, all associated tasks will become inactive.</p>
              <label for="swal-input-fn-name" style="display:block; margin-bottom:6px;">Type the function name to confirm:</label>
              <input id="swal-input-fn-name" class="swal2-input" style="margin: 0;" placeholder="Type function name to confirm" />
              <label style="display:flex; align-items:center; justify-content:center; margin-top:12px;">
                <input type="checkbox" id="swal-input-accept" style="margin-right:8px;" />
                <span>I understand and want to proceed</span>
              </label>
            </div>
          `,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Proceed",
          confirmButtonColor: "#d33",
          cancelButtonText: "Cancel",
          preConfirm: () => {
            const popup = Swal.getPopup();
            const nameInput = popup.querySelector("#swal-input-fn-name");
            const acceptCheckbox = popup.querySelector("#swal-input-accept");
            if (nameInput.value !== fn.name) {
              Swal.showValidationMessage(
                "Please type the correct function name to proceed"
              );
              return false;
            }
            if (!acceptCheckbox.checked) {
              Swal.showValidationMessage(
                "Please confirm by checking the checkbox to proceed"
              );
              return false;
            }
            // Validations passed -> apply the inactivation
            setFunctions((prev) =>
              prev.map((f) =>
                f.id === id ? { ...f, is_active: false } : f
              )
            );
            return true;
          },
        });
        return; // Don't change status yet
      }
    }
    // Directly change when: activating (inactive→active) OR task_count === 0
    setFunctions((prev) =>
      prev.map((fn) => (fn.id === id ? { ...fn, is_active: isActive } : fn))
    );
  };

  const handleOwnerChange = (fnId, selected) => {
    const list = Array.isArray(selected)
      ? selected
      : selected
      ? [selected]
      : [];
    const activeOrder = list.map((o) => ({
      value: o.value,
      label: o.label,
      is_active: true,
    }));
    setFunctions((prev) =>
      prev.map((fn) =>
        fn.id === fnId ? { ...fn, assigned_to: activeOrder } : fn
      )
    );
  };

  const handleSave = () => {
    const bi_id = localStorage.getItem("bi_id");
    const product_id = localStorage.getItem("product_id");
    const dataToSave = functions.map((fn) => ({
      function_id: fn.id,
      bi_id,
      product_id,
      function_name: fn.name,
      is_active: fn.is_active,
      assigned_to: (fn.assigned_to || []).map((a) => ({
        key: a.value,
        is_active: a.is_active,
        label: a.label,
      })),
    }));
    dispatch(postStrategyFunctions(dataToSave, "function", true));
    
  };

  const availableMasterFunctions = getFunctionsData?.available_master_functions || [];

  const filteredMasterFunctions = useMemo(() => {
    if (!masterSearchTerm.trim()) return availableMasterFunctions;
    return availableMasterFunctions.filter((fn) =>
      fn.name.toLowerCase().includes(masterSearchTerm.toLowerCase())
    );
  }, [availableMasterFunctions, masterSearchTerm]);

  const allMasterSelected =
    filteredMasterFunctions.length > 0 &&
    filteredMasterFunctions.every((fn) => selectedMasterIds.includes(fn.id));

  const handleMasterSelectAll = () => {
    setSelectedMasterIds(
      allMasterSelected ? [] : filteredMasterFunctions.map((fn) => fn.id)
    );
  };

  const handleMasterRowSelect = (id) => {
    setSelectedMasterIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleAddFunctionsFromMaster = () => {
    if (selectedMasterIds.length === 0) return;
    const bi_id = localStorage.getItem("bi_id");
    const product_id = localStorage.getItem("product_id");
    dispatch(postCustomerFunctionsFromMaster(bi_id, product_id, selectedMasterIds));
  };

  const handleOpenAddFuncModal = () => {
    setSelectedMasterIds([]);
    setMasterSearchTerm("");
    setShowAddFuncModal(true);
  };

  const content = (
    <motion.div
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      transition={{ duration: 0.8, ease: "easeInOut" }}
    >
      <div className="bi-functions-container">
        <div className="bi-functions-header">
          <h2 className="bi-functions-title">Function Management</h2>
          <p className="bi-functions-subtitle">
            Assign owners and manage statuses for core business functions.
          </p>
        </div>
        <div className="bi-functions-card">
          <div className="bi-functions-toolbar">
            
            <div className="bi-functions-toolbar-left">
              <Button
                className="add-functions-btn"
                onClick={handleOpenAddFuncModal}
                disabled={(selectedPermission === "write" || selectedPermission === "admin") ? false : true}
              >
                Add functions
              </Button>
              <div>
                <label className="bi-functions-toggle-wrapper">
                  <span className="bi-functions-toggle-label">Show Inactive</span>
                  <div className={`bi-functions-toggle ${showInactive ? "bi-functions-toggle--on" : ""}`} onClick={() => setShowInactive(!showInactive)}>
                    <div className="bi-functions-toggle-knob"/>
                  </div>
                </label>
              </div>
            </div>

            <div className="bi-functions-toolbar-right">

              <div className="bi-functions-search-wrapper">
                <span className="bi-functions-search-icon"> <AiOutlineSearch /> </span>
                <input className="bi-functions-search-input" type="text" placeholder="Search functions..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
              
                
              <Button
              
              className="save-button-style"
              onClick={!functions.length ? undefined : handleSave}
              disabled={
                selectedPermission === "write" || selectedPermission === "admin"
                  ? false
                  : true
              }
            >
              Save
            </Button>
              {/* <label className="bi-functions-toggle-wrapper">
                <span className="bi-functions-toggle-label">Show Inactive</span>
                <div className={`bi-functions-toggle ${showInactive ? "bi-functions-toggle--on" : ""}`} onClick={() => setShowInactive(!showInactive)}>
                  <div className="bi-functions-toggle-knob" />
                </div>
              </label> */}
            </div>
            
            
          </div>
          <div className="bi-functions-table-wrapper">
            <table className="bi-functions-table">
              <thead>
                <tr>
                  <th className="bi-functions-th-name">Function Name</th>
                  <th className="bi-functions-th-status">Status</th>
                  <th className="bi-functions-th-owner">Owner</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3} className="bi-functions-empty">Loading...</td></tr>
                ) : filteredFunctions.length === 0 ? (
                  <tr><td colSpan={3} className="bi-functions-empty">No functions found.</td></tr>
                ) : (
                  filteredFunctions.map((fn) => {
                    const activeOwners = fn.assigned_to
                      ?.filter((a) => a.is_active)
                      ?.map((u) => ({
                        value: u.value,
                        label: u.label,
                        icon: createImageFromInitials(
                          30,
                          u.label,
                          getRandomColor(u.label)
                        ),
                      }));
                    return (
                      <tr key={fn.id} className={!fn.is_active ? "bi-functions-row-inactive" : ""}>
                        <td className="bi-functions-td-name">
                          <span className="bi-functions-function-name">{fn.name}</span>
                        </td>
                        <td className="bi-functions-td-status">
                          <Select

                            isDisabled= {(selectedPermission === "write" || selectedPermission === "admin") ? false : true}

                            value={{
                              value: fn.is_active ? "active" : "inactive",
                              label: fn.is_active ? "Active" : "Inactive",
                            }}
                            options={[
                              { value: "active", label: "Active" },
                              { value: "inactive", label: "Inactive" },
                            ]}
                            onChange={(sel) =>
                              handleStatusChange(
                                fn.id,
                                sel.value === "active"
                              )
                            }
                            styles={{
                              container: (base) => ({
                                ...base,
                                minWidth: "120px",
                                width: "60%",
                              }),
                              singleValue: (base) => ({
                                ...base,
                                color: fn.is_active ? "green" : "red",
                                fontWeight: 500,
                              }),
                            }}
                          />
                        </td>
                        <td className="bi-functions-td-owner">
                          <Select
                            className="form-control selectr"
                            placeholder={`Assign owner`}
                            value={activeOwners}
                            options={empData}
                            isDisabled= {(selectedPermission === "write" || selectedPermission === "admin") ? false : true}
                            onChange={(sel) =>
                              handleOwnerChange(fn.id, sel)
                            }
                            // isMulti
                            isSearchable
                            filterOption={customFilter}
                            // styles={{
                            //   ...customStyles,
                            //   placeholder: (ds) => ({
                            //     ...ds,
                            //     textAlign: "left",
                            //     fontSize: "var(--text-font-size)",
                            //   }),
                            // }}
                            getOptionLabel={(e) => (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <img
                                  alt=""
                                  src={e.icon}
                                  style={{
                                    marginLeft: 5,
                                    marginRight: 6,
                                    height: 28,
                                    width: 28,
                                  }}
                                />
                                <span>{e.label}</span>
                              </div>
                            )}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Drawer
        sx={{
          "& .MuiDrawer-paper": {
            width: "50%",
            marginTop: "64px",
            height: "calc(100% - 64px)", // keep paper ending at viewport bottom (MUI sets top:0/height:100%)
            display: "flex",
            flexDirection: "column",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "transparent",
            },
          },
        }}
        PaperProps={{
          sx: {
            boxShadow: "none",
            borderLeft: "1px solid lightGray",
          },
        }}
        anchor="right"
        transitionDuration={{ enter: 500, exit: 300 }}
        SlideProps={{
          easing: {
            enter: "cubic-bezier(0.0, 0, 0.2, 1)",
            exit: "cubic-bezier(0.4, 0.0, 1, 1)",
          },
        }}
        open={showAddFuncModal}
        onClose={() => setShowAddFuncModal(false)}
      >
        <div className="add-user-sidebar-header">
          <h3>Add Functions</h3>
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <button
              className="sidebar-add-btn"
              onClick={handleAddFunctionsFromMaster}
              disabled={selectedMasterIds.length === 0}
            >
              Add ({selectedMasterIds.length})
            </button>
            <button className="sidebar-close-btn" onClick={() => setShowAddFuncModal(false)}>&times;</button>
          </div>
        </div>
        <div className="add-user-sidebar-body">
          {availableMasterFunctions.length > 0 ? (
            <>
              <div className="user-search-wrapper">
                <AiOutlineSearch className="user-search-icon" />
                <input
                  type="text"
                  className="user-search-input"
                  placeholder="Search functions..."
                  value={masterSearchTerm}
                  onChange={(e) => setMasterSearchTerm(e.target.value)}
                  style={{ marginBottom: "10px" }}
                />
              </div>
              <div className="select-all-item" onClick={handleMasterSelectAll}>
                <input
                  type="checkbox"
                  className="user-checkbox"
                  checked={filteredMasterFunctions.length > 0 && filteredMasterFunctions.every((fn) => selectedMasterIds.includes(fn.id))}
                  onChange={handleMasterSelectAll}
                />
                <span className="select-all-label">Select All</span>
                <span className="select-all-count">({selectedMasterIds.length}/{filteredMasterFunctions.length})</span>
              </div>
              <div className="select-all-divider"></div>
              <div className="add-user-sidebar-list">
                {filteredMasterFunctions.length > 0 ? (
                  filteredMasterFunctions.map((fn) => (
                    <div
                      key={fn.id}
                      className={`add-user-item ${selectedMasterIds.includes(fn.id) ? "selected" : ""}`}
                      onClick={() => handleMasterRowSelect(fn.id)}
                    >
                      <input
                        type="checkbox"
                        className="user-checkbox"
                        checked={selectedMasterIds.includes(fn.id)}
                        onChange={() => handleMasterRowSelect(fn.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="user-name">{fn.name}</span>
                    </div>
                  ))
                ) : (
                  <div className="user-empty">No functions match your search.</div>
                )}
              </div>
            </>
          ) : (
            <div className="user-empty">No functions available to add.</div>
          )}
        </div>
      </Drawer>
      {selectedHelpInfoSidebar && <SideNavHelpInfo />}
    </motion.div>
  );

  if (!localStorage.getItem("authenticated")) {
    return <div><Login /></div>;
  }

  return (
    <>
      <MainComponent
        maincontent={content}
        handleNext={() => navigate("/dashboard", { replace: true })}
        handleBackButton={() => navigate("/biproductdetails", { replace: true })}
        showSelector={true}
        showButtons={true}
        showProducts={true}
        showTemplate={false}
        // showMenu={true}
        breadcrumbParent={"Onboarding"}
        breadcrumbChild={"BI Functions"}
        documentName={"BI Functions"}
        helpKey={keys["BI Functions"].Name}
        image={"question"}
      />
    </>
  );
}

export default BiFunctions;

